import { countries, CountryCode } from "@redotech/locale/countries";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useMemo, useState } from "react";
import { BadgeRedoListItem } from "./arbiter-components/list/redo-list";
import { RedoModal } from "./arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "./arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "./flex";

export const CountrySelectionModal = memo(function CountrySelectionModal({
  selectedCountries,
  excludedCountries,
  primaryButtonText = "Save",
  onCountriesSelected,
  onClose,
  secondaryAction,
}: {
  selectedCountries: CountryCode[];
  /** Countries to omit from the picker entirely, e.g. already-covered or
   * home/domestic countries that aren't valid selections. */
  excludedCountries?: CountryCode[];
  /** Primary (Save) button text. */
  primaryButtonText?: string;
  onCountriesSelected: (countries: CountryCode[]) => void;
  onClose: () => void;
  /** Replaces the default Cancel button with a second save action, e.g.
   * "Save as Draft" alongside a "Save & Publish" primary. */
  secondaryAction?: {
    text: string;
    onSelected: (countries: CountryCode[]) => void;
  };
}) {
  const [localSelectedCountries, setLocalSelectedCountries] =
    useState<CountryCode[]>(selectedCountries);

  const countryOptions: BadgeRedoListItem<CountryCode>[] = useMemo(() => {
    const excluded = new Set(excludedCountries ?? []);
    return countries
      .filter((country) => !excluded.has(country.code))
      .map((country) => ({
        type: "badge",
        id: country.code,
        value: country.code,
        badge: { text: country.name, color: "gray" },
      }));
  }, [excludedCountries]);

  const selectedOptions = useMemo(() => {
    return countryOptions.filter((option) =>
      localSelectedCountries.includes(option.value),
    );
  }, [countryOptions, localSelectedCountries]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<CountryCode>[]) => {
      setLocalSelectedCountries(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCountriesSelected(localSelectedCountries);
  });

  const noSelectionMessage =
    localSelectedCountries.length === 0
      ? "Must select at least one country"
      : undefined;

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: primaryButtonText,
        onClick: handleSave,
        disabled: noSelectionMessage,
      }}
      secondaryButton={
        secondaryAction
          ? {
              text: secondaryAction.text,
              onClick: () => secondaryAction.onSelected(localSelectedCountries),
              disabled: noSelectionMessage,
            }
          : { text: "Cancel", onClick: onClose }
      }
      title="Select Countries"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<CountryCode>
          label="Countries"
          options={countryOptions}
          placeholder="Select countries"
          selectedOptions={selectedOptions}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
